// utils/presence.js — "ใครเปิดจอนี้อยู่" + "ใบนี้มีคนกำลังแก้" (REALTIME_DESIGN.md §3 ข้อ 1-2)
// -----------------------------------------------------------------------------
// ปัญหาที่แก้: แอดมิน 2 คนเปิดใบเดียวกัน คนหลังบันทึกทับคนแรกเงียบ ๆ — audit trail รู้ทีหลัง
// แต่ไม่ได้กันตอนเกิด · จุดที่มีค่าที่สุดคือหน้ารับเงิน (กันรับเงินซ้ำ)
//
// หลักการที่ห้ามละเมิด:
//  1. **เตือนอย่างเดียว ห้ามบล็อกปุ่มบันทึก** — สถานะอยู่ในหน่วยความจำของ process เดียว
//     ถ้า DO ขยายเป็นหลาย instance ล็อกจะมองไม่เห็นกันเอง กลายเป็นกันได้บ้างไม่ได้บ้าง
//     แบบเดาไม่ถูก ซึ่งแย่กว่าไม่กันเลย · การกันจริงคือ audit trail + idempotency key (เฟส 3)
//  2. **ไม่มีอะไรที่นี่กันคนร้ายได้** — มันคือป้ายบอกเพื่อนร่วมงาน ไม่ใช่ชั้นความปลอดภัย
//     ฝั่งเซิร์ฟเวอร์ทิ้งข้อความจากเครื่องลูกค้าทั้งหมด และไม่ส่งชื่อสตาฟไปหาลูกค้าเลย
//  3. **สายขาด = ล็อกหายเอง** จึงไม่ต้องมีปุ่ม "ปลดล็อก" และไม่มีล็อกค้างข้ามวัน
// -----------------------------------------------------------------------------
import live from './live';

let scope = null;
let editing = null;      // ใบที่เปิด dialog แก้อยู่ — ต้องจำไว้เพื่อประกาศคืนตอนต่อสายใหม่
let token = 0;           // กันจอเก่าที่ unmount ทีหลังไปลบ scope ของจอใหม่
const state = { viewers: [], editors: {} };
const subs = new Set();

function emit() {
  for (const fn of subs) {
    try { fn({ viewers: state.viewers, editors: state.editors }); } catch (e) { /* จอหนึ่งพังต้องไม่ลามไปจออื่น */ }
  }
}

live.onSystem((msg) => {
  if (!msg) return;
  // ต่อสายใหม่ (เน็ตกระตุก/กลับมาจากพื้นหลัง) = เซิร์ฟเวอร์ลืมเราหมดแล้ว
  // **ต้องประกาศคืนทั้ง view และ edit**
  // ถ้าคืนแค่ view ล็อกจะหายจากจอคนอื่นทั้งที่ dialog ยังเปิดค้างอยู่ตรงหน้า — แล้วอีกคน
  // เปิดใบเดียวกันโดยไม่มีอะไรเตือน = เคสรับเงินซ้ำแบบเป๊ะ ๆ ที่ฟีเจอร์นี้มีไว้กัน
  if (msg.topic === 'live-open') {
    if (scope) live.say({ t: 'view', key: scope });
    if (scope && editing) live.say({ t: 'edit', key: `${scope}:${editing}` });
    return;
  }
  if (msg.topic !== 'presence') return;
  if (msg.scope !== scope) return;          // ค้างมาจากจอก่อนหน้า — ทิ้ง
  state.viewers = msg.viewers || [];
  state.editors = msg.editors || {};
  emit();
});

/**
 * เข้าจอ — เรียกตอน mounted · `key` คือชื่อ state ของจอ เช่น 'customercourse'
 * คืน token ที่ต้องส่งกลับมาตอน `leave()` — ถ้าจอเก่า unmount **หลัง** จอใหม่ mount แล้ว
 * (เกิดได้ถ้ามีจอที่ใช้ `unmounted` ปนกับ `beforeUnmount`) `leave()` ของจอเก่าจะถูกเมิน
 * แทนที่จะไปลบ scope ของจอใหม่ทิ้ง ซึ่งทำให้ป้ายหายทั้งจอโดยไม่มี error ให้เห็น
 */
function enter(key) {
  scope = key;
  editing = null;
  state.viewers = [];
  state.editors = {};
  emit();
  live.say({ t: 'view', key });
  return ++token;
}

/** ออกจากจอ — เรียกตอน unmount เสมอ ไม่งั้นชื่อค้างบนจอคนอื่นจนกว่าสายจะขาด */
function leave(myToken) {
  if (myToken !== undefined && myToken !== token) return;   // จอใหม่เข้ามาแล้ว อย่าไปลบของเขา
  // ส่ง `edit:null` เฉพาะตอนที่ถือล็อกอยู่จริง — เพดานอัตราฝั่งเซิร์ฟเวอร์นับทุกข้อความ
  // การยิงทิ้งทุกครั้งที่เปลี่ยนหน้าจะเผาโควตาจนข้อความที่สำคัญถูกทิ้ง
  if (editing) live.say({ t: 'edit', key: null });
  live.say({ t: 'view', key: null });
  scope = null;
  editing = null;
  state.viewers = [];
  state.editors = {};
  emit();
}

/** เปิด dialog แก้ใบไหน — `refer` คือเลขที่ใบ · ส่ง null ตอนปิด dialog */
function edit(refer) {
  editing = refer && scope ? refer : null;
  live.say({ t: 'edit', key: editing ? `${scope}:${editing}` : null });
}

function subscribe(fn) {
  subs.add(fn);
  fn({ viewers: state.viewers, editors: state.editors });
  return () => subs.delete(fn);
}

export default { enter, leave, edit, subscribe };
